import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/feature/JsonLd';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { getComic, comics } from '@/mocks/comics';
import { getChapters } from '@/mocks/chapters';
import { getCategory } from '@/mocks/categories';
import { formatViews, formatCount } from '@/utils/format';

export default function ComicDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const comic = getComic(id);
  const [ascOrder, setAscOrder] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);

  useSeo({
    title: comic ? `《${comic.title}》漫画-免费阅读${comic.category}韩国漫画-5177漫画网` : '漫画详情-5177漫画网',
    description: comic
      ? `${comic.description} 来5177漫画网免费阅读《${comic.title}》韩国漫画，全集中文故事在线浏览。`
      : '5177漫画网免费韩国漫画阅读平台',
    keywords: comic
      ? `${comic.title}漫画,韩国漫画,韩漫,${comic.tags.join(',')},免费阅读,5177漫画网`
      : '韩国漫画,韩漫,免费阅读,5177漫画网',
    ogType: 'article',
    ogImage: comic?.cover,
    canonicalPath: `/comic/${id}`,
  });

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true" />
          <h1 className="mt-4 font-display text-2xl text-foreground-950">没有找到这部漫画</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已经下架或链接有误，去看看其他精彩漫画吧</p>
          <Link
            to="/category/all"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600"
          >
            浏览漫画大全
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategory(comic.category);
  const chapters = getChapters(comic.id);
  const orderedChapters = ascOrder ? [...chapters] : [...chapters].reverse();
  const similarComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 5);
  const fillComics = comics.filter((c) => c.category !== comic.category && c.id !== comic.id).slice(0, 4);
  const similar = similarComics.length >= 4 ? similarComics : [...similarComics, ...fillComics].slice(0, 5);
  const lastChapter = chapters[chapters.length - 1];

  const handleShare = () => {
    setShowShare(!showShare);
    setCopied(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}/comic/${comic.id}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${category.name}漫画`,
        item: `${SITE_URL}/category/${comic.category}`,
      },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `${SITE_URL}/comic/${comic.id}` },
    ],
  };

  const comicLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicStory',
    '@id': `${SITE_URL}/comic/${comic.id}#comic`,
    url: `${SITE_URL}/comic/${comic.id}`,
    name: comic.title,
    alternativeName: comic.originalTitle,
    description: comic.description,
    image: comic.cover,
    genre: category.name,
    author: { '@type': 'Person', name: comic.author },
    datePublished: `${comic.year}-01-01`,
    inLanguage: 'zh-CN',
    numberOfEpisodes: chapters.length,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: comic.likes,
    },
    isPartOf: { '@id': `${SITE_URL}/#website` },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={[breadcrumbLd, comicLd]} />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: `${category.name}漫画`, to: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <div className="mt-6 flex flex-col sm:flex-row gap-6 md:gap-8">
            <div className="w-44 sm:w-48 md:w-52 shrink-0 mx-auto sm:mx-0">
              <div className="relative aspect-[2/3] overflow-hidden rounded-lg hover-lift">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title}漫画-免费阅读`}
                  className="h-full w-full object-cover object-top"
                />
                <span className="absolute left-2 top-2 rounded-md bg-background-50/90 px-2 py-0.5 text-xs text-primary-700 font-medium backdrop-blur-sm">
                  {category.name}
                </span>
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-display text-3xl md:text-4xl text-foreground-950">
                {comic.title}
              </h1>
              <p className="mt-1.5 text-sm text-foreground-600">
                {comic.originalTitle} · {comic.year}年
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-user-line text-foreground-500" aria-hidden="true" />
                  {comic.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-time-line text-foreground-500" aria-hidden="true" />
                  {comic.status}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-book-2-line text-foreground-500" aria-hidden="true" />
                  共{chapters.length}话
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                  <strong className="text-foreground-950">{comic.rating.toFixed(1)}</strong>分
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line text-foreground-500" aria-hidden="true" />
                  {formatViews(comic.views)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-heart-3-line text-primary-500" aria-hidden="true" />
                  {formatCount(comic.likes)}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <p className="mt-5 max-w-3xl text-sm md:text-base leading-7 md:leading-8 text-foreground-700">
                {comic.description}
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.id}/chapter/${chapters[0]?.id}`}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-open-line text-base" aria-hidden="true" />
                  开始阅读
                </Link>
                {lastChapter && (
                  <Link
                    to={`/comic/${comic.id}/chapter/${lastChapter.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-secondary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-secondary-600"
                  >
                    <i className="ri-play-circle-line text-base" aria-hidden="true" />
                    继续阅读 · 最新{lastChapter.title}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setFavorited(!favorited)}
                  className={`inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors border ${
                    favorited
                      ? 'bg-primary-100 text-primary-700 border-primary-200'
                      : 'bg-background-50 text-foreground-700 border-background-300 hover:bg-background-100'
                  }`}
                  aria-pressed={favorited}
                >
                  <i className={`${favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-base`} aria-hidden="true" />
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-background-50 text-foreground-700 border border-background-300 px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors hover:bg-background-100"
                    aria-expanded={showShare}
                  >
                    <i className="ri-share-forward-line text-base" aria-hidden="true" />
                    分享
                  </button>
                  {showShare && (
                    <div className="absolute right-0 top-full z-20 mt-2 w-64 rounded-lg bg-background-50 border border-background-200 p-3 animate-fade-in">
                      <p className="text-xs text-foreground-600">分享这部漫画给朋友</p>
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          readOnly
                          value={`${SITE_URL}/comic/${comic.id}`}
                          className="w-full rounded-md border border-background-200 bg-background-100 px-2.5 py-1.5 text-xs text-foreground-800"
                          aria-label="漫画链接"
                        />
                        <button
                          type="button"
                          onClick={handleCopy}
                          className="shrink-0 rounded-md bg-primary-500 px-3 py-1.5 text-xs text-background-50 whitespace-nowrap hover:bg-primary-600"
                        >
                          {copied ? '已复制' : '复制'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
        <section aria-labelledby="chapter-list-title">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="chapter-list-title" className="flex items-center gap-2 font-display text-2xl md:text-3xl">
              <span className="text-primary-500">
                <i className="ri-list-ordered-2 text-2xl md:text-3xl" aria-hidden="true" />
              </span>
              章节列表
              <span className="text-sm font-normal text-foreground-500">共{chapters.length}话</span>
            </h2>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {(['newest', 'oldest'] as const).map((order) => (
                <button
                  key={order}
                  type="button"
                  onClick={() => setAscOrder(order === 'oldest')}
                  className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                    ascOrder === (order === 'oldest')
                      ? 'bg-background-50 text-primary-600 font-medium'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {order === 'newest' ? '最新在前' : '最早在前'}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {orderedChapters.map((ch) => (
              <Link
                key={ch.id}
                to={`/comic/${comic.id}/chapter/${ch.id}`}
                className="group flex items-center justify-between gap-2 rounded-md bg-background-100 px-3.5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                <span className="truncate">{ch.title}</span>
                <span className="flex shrink-0 items-center gap-1 text-xs text-foreground-500 group-hover:text-background-100">
                  <i className="ri-eye-line" aria-hidden="true" />
                  {formatViews(ch.views)}
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12 md:mt-16" aria-labelledby="similar-title">
          <div className="flex items-end justify-between">
            <h2 id="similar-title" className="flex items-center gap-2 font-display text-2xl md:text-3xl">
              <span className="text-accent-500">
                <i className="ri-thumb-up-line text-2xl md:text-3xl" aria-hidden="true" />
              </span>
              相似漫画推荐
            </h2>
            <Link
              to={`/category/${comic.category}`}
              className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              更多{category.name}漫画
              <i className="ri-arrow-right-line ml-1" aria-hidden="true" />
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {similar.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}